<p-toast></p-toast>

<app-page-shell>
  <app-page-hero
    eyebrow="CRM Quotations"
    title="Create Sales Order"
    subtitle="Promote accepted quotation pricing into a controlled draft sales order.">
    <div actions>
      <p-button label="Refresh Preview" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="loading()" (onClick)="loadPreview()"></p-button>
      <p-button label="Create Sales Order" icon="pi pi-file-plus" [disabled]="!isReady() || converting()" [loading]="converting()" (onClick)="convert()"></p-button>
    </div>
  </app-page-hero>

  @if (preview()?.quotation; as quote) {
    <app-page-data-panel title="Quotation Snapshot" [subtitle]="quote.subject" [resultText]="quote.status">
      <div class="snapshot-grid">
        <div><span>Quotation</span><b>{{ quote.quotationNumber || quote.id }}</b></div>
        <div><span>Customer</span><b>{{ quote.customerName || '-' }}</b></div>
        <div><span>Total</span><b>{{ quote.currencyCode || 'SAR' }} {{ quote.totalAmount || 0 | number:'1.2-2' }}</b></div>
        <div><span>Validity</span><b>{{ quote.startDate | date:'mediumDate' }} - {{ quote.expiryDate | date:'mediumDate' }}</b></div>
      </div>
    </app-page-data-panel>
  }

  <app-page-form-section icon="pi pi-sliders-h" title="Conversion Controls" subtitle="Resolve customer, payment term, selected lines, and override context before creating the draft order.">
    <div class="form-grid">
      <label>
        <span>Customer</span>
        <p-select [options]="customers" optionLabel="name" optionValue="id" [(ngModel)]="request.customerId" (onChange)="loadPreview()" [showClear]="true" styleClass="w-full"></p-select>
      </label>
      <label>
        <span>Payment Term</span>
        <p-select [options]="paymentTerms" optionLabel="name" optionValue="paymentTermID" [(ngModel)]="request.paymentTermId" (onChange)="loadPreview()" [showClear]="true" styleClass="w-full"></p-select>
      </label>
      <label>
        <span>Logistics Project</span>
        <app-logistics-project-lookup [companyId]="companyId" [(ngModel)]="request.logisticsProjectId"
          (ngModelChange)="loadPreview()"></app-logistics-project-lookup>
      </label>
      <label>
        <span>Expected Date</span>
        <p-datePicker [(ngModel)]="request.expectedDate" appendTo="body" styleClass="w-full"></p-datePicker>
      </label>
      @if (preview()?.overrideRequired) {
        <label class="full-width">
          <span>Override Reason</span>
          <textarea pTextarea [(ngModel)]="request.overrideReason" (blur)="loadPreview()" rows="3" class="w-full" placeholder="Required for non-approved, expired, or duplicate quotation conversion."></textarea>
        </label>
      }
    </div>
  </app-page-form-section>

  @if ((preview()?.warnings || []).length) {
    <app-page-data-panel title="Conversion Warnings" subtitle="Errors block conversion; warnings require an override where shown." [resultText]="(preview()?.warnings?.length || 0) + ' warnings'">
      <div class="warning-list">
        @for (warning of preview()?.warnings || []; track $index) {
          <div class="warning-row">
            <p-tag [severity]="warningSeverity(warning)" [value]="warning.severity || 'INFO'"></p-tag>
            <div>
              <b>{{ warning.code }}</b>
              <span>{{ warning.message }}</span>
            </div>
          </div>
        }
      </div>
    </app-page-data-panel>
  }

  @if ((preview()?.existingSalesOrders || []).length) {
    <app-page-data-panel title="Existing Sales Orders" subtitle="Active sales orders already linked to this quotation." [resultText]="(preview()?.existingSalesOrders?.length || 0) + ' linked'">
      <div class="linked-orders">
        @for (order of preview()?.existingSalesOrders || []; track order.orderID) {
          <a [routerLink]="['/order/sales-order/view', order.orderID]">
            <i class="pi pi-file-edit"></i>
            <span>{{ order.orderNumber || ('Sales Order #' + order.orderID) }}</span>
            <p-tag [value]="order.state || 'DRAFT'" severity="info"></p-tag>
          </a>
        }
      </div>
    </app-page-data-panel>
  }

  <app-page-data-panel title="Sales Order Lines" subtitle="Selected quotation lines and installation rows will become draft sales order lines." [resultText]="selectedCount() + ' selected'">
    <p-table [value]="preview()?.proposedLines || []" styleClass="p-datatable-sm p-datatable-gridlines">
      <ng-template pTemplate="header">
        <tr>
          <th style="width: 4rem">Use</th>
          <th>Product</th>
          <th class="text-right">Qty</th>
          <th class="text-right">Unit</th>
          <th class="text-right">Tax</th>
          <th class="text-right">Total</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-line>
        <tr>
          <td><p-checkbox [binary]="true" [ngModel]="isLineSelected(line)" (ngModelChange)="toggleLine(line, $event)"></p-checkbox></td>
          <td>
            <strong>{{ line.product?.name || line.description || '-' }}</strong>
            <small>{{ lineSourceLabel(line) }}</small>
          </td>
          <td class="text-right">{{ line.quantity | number:'1.0-2' }}</td>
          <td class="text-right">{{ preview()?.currencyCode || 'SAR' }} {{ line.unitPrice | number:'1.2-2' }}</td>
          <td class="text-right">{{ preview()?.currencyCode || 'SAR' }} {{ line.totalTax | number:'1.2-2' }}</td>
          <td class="text-right"><strong>{{ preview()?.currencyCode || 'SAR' }} {{ line.totalLineAmount | number:'1.2-2' }}</strong></td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr><td colspan="6">No convertible quotation lines or installation rows found.</td></tr>
      </ng-template>
    </p-table>
  </app-page-data-panel>
</app-page-shell>
